import { genFileId } from '@zoryn/components/upload'
import { isCommonResultSuccess } from '@zoryn/network'
import { isArray, isObject, isString } from '@zoryn/utils'

import type { UploadUserFile } from '@zoryn/components/upload'

const FILE_RESPONSE_PATH_KEYS = ['path'] as const
const RUSTFS_UNIQUE_FILENAME = /(?:^|\/)([a-f0-9]{32})_(.+)$/i
const FILE_RESPONSE_ID_KEYS = ['id', 'fileId'] as const
const FILE_RESPONSE_NAME_KEYS = ['name', 'fileName', 'filename'] as const
const FILE_RESPONSE_URL_KEYS = ['url', 'fileUrl', 'path', 'link'] as const

export type GridFileCellMeta = {
  id?: string | number
  name?: string
  url?: string
}

export type GridFilesCellMeta = {
  files?: GridFileCellMeta[]
}

function pickFromRecord(
  record: Record<string, unknown>,
  keys: readonly string[]
): string | null {
  for (const key of keys) {
    const value = record[key]
    if (value == null || value === '') continue
    return String(value)
  }
  return null
}

function unwrapResponseData(response: unknown): Record<string, unknown> | null {
  if (!isObject(response)) return null
  const record = response as Record<string, unknown>
  if (
    ('code' in record || 'success' in record) &&
    !isCommonResultSuccess(record)
  ) {
    return null
  }
  const data = record.data ?? record.result
  if (isObject(data)) return data as Record<string, unknown>
  if (isString(data) && data.trim()) {
    return { url: data.trim() }
  }
  return record
}

export function resolveUploadPathWithFilename(
  dirPath: string,
  filename: string
): string {
  const dir = String(dirPath ?? '').trim()
  const safeName =
    String(filename ?? 'file')
      .replace(/[/\\]/g, '_')
      .trim() || 'file'
  if (!dir) return safeName
  const normalizedDir = dir.endsWith('/') ? dir : `${dir}/`
  return `${normalizedDir}${safeName}`
}

/** 从上传接口响应中解析文件 ID */
export function resolveUploadResponseFileId(
  response: unknown
): string | number | null {
  const record = unwrapResponseData(response)
  if (!record) return null
  const id = pickFromRecord(record, FILE_RESPONSE_ID_KEYS)
  if (!id) return null
  const numeric = Number(id)
  return Number.isFinite(numeric) ? numeric : id
}

/** 从 RustFS 存储 key 解析用户原始文件名，如 uuid_2.xlsx → 2.xlsx */
export function extractOriginalFilenameFromStorageKey(path: unknown): string {
  if (!isString(path) || !path.trim()) return ''
  const segment = path.trim().split('/').pop() ?? path.trim()
  const matched = segment.match(RUSTFS_UNIQUE_FILENAME)
  if (matched?.[2]) return matched[2]

  const underscoreIndex = segment.indexOf('_')
  if (underscoreIndex > 0) {
    const prefix = segment.slice(0, underscoreIndex)
    if (/^[a-f0-9]{16,32}$/i.test(prefix)) {
      return segment.slice(underscoreIndex + 1)
    }
  }

  return segment
}

/** 从上传接口响应中解析文件展示信息 */
export function resolveUploadResponseFileMeta(
  response: unknown
): GridFileCellMeta | null {
  const record = unwrapResponseData(response)
  if (!record) return null

  const id = pickFromRecord(record, FILE_RESPONSE_ID_KEYS)
  const path = pickFromRecord(record, FILE_RESPONSE_PATH_KEYS)
  const name =
    pickFromRecord(record, FILE_RESPONSE_NAME_KEYS) ||
    extractOriginalFilenameFromStorageKey(path)
  const url = pickFromRecord(record, FILE_RESPONSE_URL_KEYS)

  if (!id && !name && !url) return null
  return {
    id: id ? (Number.isFinite(Number(id)) ? Number(id) : id) : undefined,
    name: name ?? undefined,
    url: url ?? undefined,
  }
}

export function resolveFileCellDisplayName(
  value: unknown,
  meta?: GridFileCellMeta | null
): string {
  if (meta?.name) return meta.name
  if (isObject(meta) && isString((meta as { name?: string }).name)) {
    return (meta as { name: string }).name
  }
  if (value == null || value === '') return ''
  if (isObject(value)) {
    const name = (value as { name?: unknown }).name
    if (isString(name) && name.trim()) return name.trim()
    const url = (value as { url?: unknown }).url
    if (isString(url) && url.trim()) return getFileNameFromUrl(url.trim())
  }
  if (isString(value) && /^https?:\/\//i.test(value)) {
    return getFileNameFromUrl(value)
  }
  if (value != null && value !== '') {
    return `文件#${String(value)}`
  }
  return ''
}

function getFileNameFromUrl(url: string): string {
  try {
    const pathname = new URL(url, 'http://localhost').pathname
    return pathname.split('/').pop() || url
  } catch {
    return url.split('/').pop() || url
  }
}

function normalizeFileMeta(value: unknown): GridFileCellMeta | null {
  if (!isObject(value)) return null
  const record = value as GridFileCellMeta
  if (record.id == null && !record.name && !record.url) return null
  return record
}

function resolveFileCellFallbackName(
  row: Record<string, unknown>,
  field: string
): string {
  if (field === 'importTemplateFileId') {
    const filename = row.templateFilename
    if (isString(filename) && filename.trim()) return filename.trim()
    const templateName = row.templateName
    if (isString(templateName) && templateName.trim()) {
      return templateName.includes('.')
        ? templateName.trim()
        : `${templateName.trim()}.xlsx`
    }
  }

  const base = field.replace(/Ids?$/, '')
  for (const suffix of ['Filename', 'FileName', 'Name']) {
    const value = row[`${base}${suffix}`]
    if (isString(value) && value.trim()) return value.trim()
  }
  return ''
}

function parseFileIdList(value: unknown): Array<string | number> {
  if (value == null || value === '') return []
  if (isArray(value)) {
    return value.filter((item) => item != null && item !== '')
  }
  if (isString(value)) {
    const trimmed = value.trim()
    if (!trimmed) return []
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed) as unknown
        if (isArray(parsed)) {
          return parseFileIdList(parsed)
        }
      } catch {
        // ignore
      }
    }
    if (trimmed.includes(',')) {
      return trimmed
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
    }
    return [trimmed]
  }
  return [value as string | number]
}

/** 解析单元格 Meta 列表（单文件 / 多附件） */
export function resolveFileCellMetaList(
  row: Record<string, unknown>,
  field: string,
  columnType?: string
): GridFileCellMeta[] {
  const metaKey = `${field}Meta`
  const rawMeta = row[metaKey]

  if (isObject(rawMeta) && isArray((rawMeta as GridFilesCellMeta).files)) {
    return ((rawMeta as GridFilesCellMeta).files ?? [])
      .map(normalizeFileMeta)
      .filter((item): item is GridFileCellMeta => !!item)
  }

  if (columnType === 'files') {
    const ids = parseFileIdList(row[field])
    return ids.map((id) => ({ id, name: `文件#${String(id)}` }))
  }

  const single = normalizeFileMeta(rawMeta)
  if (single) {
    if (!single.name) {
      const fallbackName = resolveFileCellFallbackName(row, field)
      if (fallbackName) {
        return [{ ...single, name: fallbackName }]
      }
    }
    return [single]
  }

  const val = row[field]
  if (val == null || val === '') return []
  const fallbackName = resolveFileCellFallbackName(row, field)
  const name = fallbackName || resolveFileCellDisplayName(val, null)
  if (!name) return []
  return [{ id: val as string | number, name }]
}

export function resolveFileCellSummaryLabel(files: GridFileCellMeta[]): string {
  if (!files.length) return ''
  if (files.length === 1) {
    return (
      files[0]?.name || resolveFileCellDisplayName(files[0]?.id, files[0]) || ''
    )
  }
  const first = files[0]
  const firstName =
    first?.name || resolveFileCellDisplayName(first?.id, first) || '文件'
  return `${firstName} +${files.length - 1}`
}

export function isGridFileDownloadable(
  meta: GridFileCellMeta,
  downloadEnabled = true
): boolean {
  return downloadEnabled && !!meta.url
}

/** 触发浏览器下载/打开文件 */
export function downloadGridFile(meta: GridFileCellMeta) {
  if (!meta.url) return
  const link = document.createElement('a')
  link.href = meta.url
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  if (meta.name) {
    link.download = meta.name
  }
  link.click()
}

/** 批量打包下载 URL（需后端提供 batchDownloadAction） */
export function resolveBatchDownloadUrl(
  batchDownloadAction: string,
  fileIds: Array<string | number>,
  fileName = 'attachments.zip'
): string {
  const params = new URLSearchParams()
  params.set('fileIds', fileIds.map(String).join(','))
  params.set('fileName', fileName)
  const joiner = batchDownloadAction.includes('?') ? '&' : '?'
  return `${batchDownloadAction}${joiner}${params.toString()}`
}

export function downloadGridFilesBatch(
  files: GridFileCellMeta[],
  batchDownloadAction?: string,
  fileName?: string
) {
  const ids = files
    .map((file) => file.id)
    .filter((id) => id != null && id !== '')
  if (!ids.length) return
  if (batchDownloadAction) {
    window.open(
      resolveBatchDownloadUrl(batchDownloadAction, ids, fileName),
      '_blank'
    )
    return
  }
  for (const file of files) {
    if (file.url) {
      downloadGridFile(file)
    }
  }
}

export function createUploadFilesFromFileMeta(
  value: unknown,
  meta?: GridFileCellMeta | null
): UploadUserFile[] {
  const displayName = resolveFileCellDisplayName(value, meta)
  if (!displayName && (value == null || value === '')) return []

  return [
    {
      name: displayName || 'file',
      url: meta?.url,
      status: 'success' as const,
      uid: genFileId(),
    },
  ]
}

export function createUploadFilesFromFileMetaList(
  files: GridFileCellMeta[]
): UploadUserFile[] {
  return files.map((meta) => ({
    name: meta.name || resolveFileCellDisplayName(meta.id, meta) || 'file',
    url: meta.url,
    status: 'success' as const,
    uid: genFileId(),
  }))
}

export function serializeFileCellValue(
  meta: GridFileCellMeta | null
): string | number | null {
  if (!meta?.id && !meta?.url) return null
  if (meta.id != null && meta.id !== '') return meta.id
  return meta.url ?? null
}

export function serializeFilesCellValue(
  metas: GridFileCellMeta[]
): Array<string | number> | null {
  if (!metas.length) return null
  const ids = metas
    .map((meta) => meta.id)
    .filter((id): id is string | number => id != null && id !== '')
    .map((id) => {
      const numeric = Number(id)
      return Number.isFinite(numeric) ? numeric : id
    })
  return ids.length ? ids : null
}

export function buildFilesCellMeta(
  metas: GridFileCellMeta[]
): GridFilesCellMeta | null {
  if (!metas.length) return null
  return { files: metas }
}

/** 同步行上 file/files 列的 Meta（Grid 行对象为可变引用） */
export function syncFileCellMeta(
  row: Record<string, unknown>,
  field: string,
  meta: GridFileCellMeta | GridFilesCellMeta | null | undefined
) {
  const metaKey = `${field}Meta`
  if (meta == null) {
    delete row[metaKey]
    return
  }
  row[metaKey] = meta
}
